import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import { fetchUser, updateUserRole, updateUserExpiry, banUser, unbanUser, fetchUserSkills, disableUserSkill } from '@api/backoffice';
import { useTableSort } from '@hooks/useTableSort';
import ThSortable from '@components/Backoffice/ThSortable';
import ExportButton from '@components/Backoffice/ExportButton';
import { exportArrayToCsv } from '@utils/csvExport';
import {
  accountExpiryBounds,
  expiryDateInputToIso,
  isExpiryDateInBounds,
  MAX_ACCOUNT_EXPIRY_YEARS,
  toExpiryDateInputValue,
} from '@utils/accountExpiry';
import { axiosErrorMessage } from '@api/errors';
import type { UserDetail, ChangeRoleDialogProps, SetExpiryDialogProps, BanUserDialogProps, SkillResponse } from './types';
import { formatSkillStatus } from './formatters';
import UserSkillAccessDialog from './UserSkillAccessDialog';
import {
  PageTitle,
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Badge,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  ActionButton,
  ButtonGroup,
  SmallText,
  GhostLink,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  ButtonRow,
  EmptyState,
  TabBar,
  Tab,
  Table,
  ToggleButton,
  TruncatedCell,
} from '@components/Backoffice/BackofficeLayout.styled';
import { BannedBadge } from './BackofficeUserDetail.styled';

function getRoleBadgeVariant(orgRole: string): 'info' | 'success' | 'warning' {
  if (orgRole === 'AIBUILD_ADMIN') return 'info';
  if (orgRole === 'ORG_ADMIN') return 'success';
  return 'warning';
}

const canEditExpiry = (skill: SkillResponse) =>
  skill.enabled && skill.acquisitionType !== 'INCLUDED';

const BackofficeUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [showUnbanConfirm, setShowUnbanConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'skills'>('details');
  const [skills, setSkills] = useState<SkillResponse[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsLoaded, setSkillsLoaded] = useState(false);
  const [revokeSkill, setRevokeSkill] = useState<SkillResponse | null>(null);
  const [accessSkill, setAccessSkill] = useState<SkillResponse | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await fetchUser(userId);
      setUser(data);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadSkills = useCallback(async () => {
    if (!userId) return;
    setSkillsLoading(true);
    try {
      const data = await fetchUserSkills(userId);
      setSkills(data);
      setSkillsLoaded(true);
    } finally {
      setSkillsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeTab === 'skills' && !skillsLoaded) {
      loadSkills();
    }
  }, [activeTab, skillsLoaded, loadSkills]);

  const handleRevokeSkill = async (skill: SkillResponse) => {
    if (!userId) return;
    try {
      await disableUserSkill(userId, skill.skillId);
      showSuccess(`${skill.displayName} disabled`);
      setRevokeSkill(null);
      await loadSkills();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update skill');
      setRevokeSkill(null);
    }
  };

  const {
    sorted: sortedSkills,
    sortKey: skillSortKey,
    sortDirection: skillSortDir,
    toggle: skillToggle,
  } = useTableSort(skills, 'displayName');

  if (loading) return <EmptyState>Loading...</EmptyState>;
  if (!user) return <EmptyState>User not found</EmptyState>;

  const isSelf = authUser?.id === user.id;

  const renderSkillsContent = () => {
    if (skillsLoading) return <EmptyState>Loading skills...</EmptyState>;
    if (skills.length === 0) return <EmptyState>No skills defined</EmptyState>;
    return (
      <>
      <CardHeader>
        <span />
        <ExportButton
          onExport={async () =>
            exportArrayToCsv(
              sortedSkills,
              [
                { header: 'Skill', accessor: (s) => s.displayName },
                { header: 'Description', accessor: (s) => s.description || '' },
                { header: 'Status', accessor: (s) => s.status || '' },
                { header: 'Enabled', accessor: (s) => String(s.enabled) },
                { header: 'Expires', accessor: (s) => s.expiresAt ? new Date(s.expiresAt).toISOString() : '' },
              ],
              `user_skills_${userId}_${new Date().toISOString().slice(0, 10)}.csv`
            )
          }
        />
      </CardHeader>
      <Table>
        <thead>
          <tr>
            {skillTh('Skill', 'displayName')}
            {skillTh('Description', 'description')}
            {skillTh('Status', 'status')}
            {skillTh('Expires', 'expiresAt')}
            <th>Access</th>
          </tr>
        </thead>
        <tbody>
          {sortedSkills.map((skill) => (
            <tr key={skill.skillId}>
              <td><strong>{skill.displayName}</strong></td>
              <TruncatedCell>{skill.description}</TruncatedCell>
              <td>{formatSkillStatus(skill)}</td>
              <td>
                {skill.expiresAt
                  ? new Date(skill.expiresAt).toLocaleDateString()
                  : '—'}
                {canEditExpiry(skill) && (
                  <ActionButton $variant="ghost" onClick={() => setAccessSkill(skill)}>
                    Edit
                  </ActionButton>
                )}
              </td>
              <td>
                <ToggleButton
                  $active={skill.enabled}
                  onClick={() => (skill.enabled ? setRevokeSkill(skill) : setAccessSkill(skill))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      </>
    );
  };

  const skillTh = (label: string, key: string) => (
    <ThSortable
      label={label}
      sortKey={key}
      activeKey={skillSortKey}
      direction={skillSortDir}
      onToggle={skillToggle}
    />
  );

  return (
    <>
      <PageHeader>
        <ActionButton $variant="ghost" onClick={() => navigate('/backoffice-admin/users')}>
          &larr; Back
        </ActionButton>
        <PageTitle>{user.email}</PageTitle>
        <Badge $variant={getRoleBadgeVariant(user.orgRole)}>
          {user.orgRole}
        </Badge>
      </PageHeader>

      <Card>
        <CardHeader>
          <span>
            {user.displayName || user.email}
            {user.bannedAt && (
              <BannedBadge $variant="warning">BANNED</BannedBadge>
            )}
          </span>
          {!isSelf && (
            <ButtonGroup>
              <ActionButton $variant="primary" onClick={() => setShowRoleDialog(true)}>
                Change Role
              </ActionButton>
              <ActionButton $variant="primary" onClick={() => setShowExpiryDialog(true)}>
                Set Expiry
              </ActionButton>
              {user.bannedAt ? (
                <ActionButton $variant="primary" onClick={() => setShowUnbanConfirm(true)}>
                  Unban User
                </ActionButton>
              ) : (
                <ActionButton $variant="danger" onClick={() => setShowBanDialog(true)}>
                  Ban User
                </ActionButton>
              )}
            </ButtonGroup>
          )}
        </CardHeader>
        <CardBody>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>Display Name</DetailLabel>
              <DetailValue>{user.displayName || '—'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Email</DetailLabel>
              <DetailValue>{user.email}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Organization</DetailLabel>
              <DetailValue>
                <GhostLink
                  onClick={() => navigate(`/backoffice-admin/organizations/${user.organizationId}`)}
                >
                  {user.organizationName || user.organizationId}
                </GhostLink>
                <br />
                <SmallText>{user.organizationId}</SmallText>
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Expiry Date</DetailLabel>
              <DetailValue>
                {user.expiryDate ? new Date(user.expiryDate).toLocaleString() : '—'}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Last Login</DetailLabel>
              <DetailValue>
                {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>{new Date(user.createdAt).toLocaleString()}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>EULA Accepted</DetailLabel>
              <DetailValue>
                {user.eulaAcceptedAt ? new Date(user.eulaAcceptedAt).toLocaleString() : 'No'}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Cognito Sub</DetailLabel>
              <DetailValue>
                <SmallText>{user.cognitoSub}</SmallText>
              </DetailValue>
            </DetailItem>
            {user.bannedAt && (
              <>
                <DetailItem>
                  <DetailLabel>Banned At</DetailLabel>
                  <DetailValue>{new Date(user.bannedAt).toLocaleString()}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Ban Reason</DetailLabel>
                  <DetailValue>{user.banReason || '—'}</DetailValue>
                </DetailItem>
              </>
            )}
          </DetailGrid>
        </CardBody>
      </Card>

      <TabBar>
        <Tab $active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
          Details
        </Tab>
        <Tab $active={activeTab === 'skills'} onClick={() => setActiveTab('skills')}>
          Skills
        </Tab>
      </TabBar>

      {activeTab === 'skills' && (
        <Card>
          {renderSkillsContent()}
        </Card>
      )}

      {revokeSkill && (
        <DialogOverlay onClick={() => setRevokeSkill(null)}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogTitle>Revoke {revokeSkill.displayName}?</DialogTitle>
            <CardBody>
              Are you sure you want to revoke{' '}
              <strong>{revokeSkill.displayName}</strong> access for{' '}
              <strong>{user.email}</strong>?
            </CardBody>
            <ButtonRow>
              <ActionButton $variant="ghost" onClick={() => setRevokeSkill(null)}>
                Cancel
              </ActionButton>
              <ActionButton $variant="danger" onClick={() => handleRevokeSkill(revokeSkill)}>
                Revoke
              </ActionButton>
            </ButtonRow>
          </DialogContent>
        </DialogOverlay>
      )}

      {accessSkill && (
        <UserSkillAccessDialog
          userId={user.id}
          userEmail={user.email}
          skill={accessSkill}
          onClose={() => setAccessSkill(null)}
          onSave={() => {
            showSuccess(
              accessSkill.enabled
                ? `${accessSkill.displayName} expiry updated`
                : `${accessSkill.displayName} enabled`
            );
            loadSkills();
          }}
          onError={(msg) => showError(msg)}
        />
      )}

      {showRoleDialog && (
        <ChangeRoleDialog
          userId={user.id}
          currentRole={user.orgRole}
          onClose={() => setShowRoleDialog(false)}
          onSave={(newRole) => {
            showSuccess(`Role updated to ${newRole}`);
            load();
          }}
          onError={(msg) => showError(msg)}
        />
      )}
      {showExpiryDialog && (
        <SetExpiryDialog
          userId={user.id}
          currentExpiry={user.expiryDate}
          onClose={() => setShowExpiryDialog(false)}
          onSave={() => {
            showSuccess('Expiry date updated');
            load();
          }}
          onError={(msg) => showError(msg)}
        />
      )}
      {showBanDialog && (
        <BanUserDialog
          userId={user.id}
          userEmail={user.email}
          onClose={() => setShowBanDialog(false)}
          onSave={() => {
            showSuccess('User banned');
            load();
          }}
          onError={(msg) => showError(msg)}
        />
      )}
      {showUnbanConfirm && (
        <UnbanDialog
          userId={user.id}
          userEmail={user.email}
          onClose={() => setShowUnbanConfirm(false)}
          onSave={() => {
            showSuccess('User unbanned');
            load();
          }}
          onError={(msg) => showError(msg)}
        />
      )}
    </>
  );
};

function ChangeRoleDialog({
  userId,
  currentRole,
  onClose,
  onSave,
  onError,
}: ChangeRoleDialogProps) {
  const [role, setRole] = useState(currentRole);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserRole(userId, role);
      onSave(role);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Change Role</DialogTitle>
        <FormField>
          <FormLabel>Current: {currentRole}</FormLabel>
          <FormSelect value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="MEMBER">MEMBER</option>
            <option value="ORG_ADMIN">ORG_ADMIN</option>
            <option value="AIBUILD_ADMIN">AIBUILD_ADMIN</option>
          </FormSelect>
        </FormField>
        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            $variant="primary"
            onClick={handleSave}
            disabled={saving || role === currentRole}
          >
            {saving ? 'Saving...' : 'Update Role'}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

function SetExpiryDialog({
  userId,
  currentExpiry,
  onClose,
  onSave,
  onError,
}: SetExpiryDialogProps) {
  const [expiryDate, setExpiryDate] = useState(() => toExpiryDateInputValue(currentExpiry));
  const [saving, setSaving] = useState(false);
  const bounds = accountExpiryBounds();
  const inRange = isExpiryDateInBounds(expiryDate, bounds);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserExpiry(userId, expiryDateInputToIso(expiryDate));
      onSave();
      onClose();
    } catch (err) {
      onError(axiosErrorMessage(err, 'Failed to update expiry date'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Set Expiry Date</DialogTitle>
        <FormField>
          <FormLabel htmlFor="backoffice-expiry-date">Expiry Date</FormLabel>
          <FormInput
            id="backoffice-expiry-date"
            type="date"
            min={bounds.min}
            max={bounds.max}
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
          <SmallText>Up to {MAX_ACCOUNT_EXPIRY_YEARS} years from today</SmallText>
        </FormField>
        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            $variant="primary"
            onClick={handleSave}
            disabled={saving || !inRange}
          >
            {saving ? 'Saving...' : 'Update Expiry'}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

function UnbanDialog({ userId, userEmail, onClose, onSave, onError }: Omit<BanUserDialogProps, 'userId'> & { userId: string }) {
  const [saving, setSaving] = useState(false);

  const handleUnban = async () => {
    setSaving(true);
    try {
      await unbanUser(userId);
      onSave();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to unban user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={saving ? undefined : onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Unban {userEmail}?</DialogTitle>
        <CardBody>
          This will restore full access to the account.
        </CardBody>
        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </ActionButton>
          <ActionButton $variant="primary" onClick={handleUnban} disabled={saving}>
            {saving ? 'Unbanning...' : 'Unban'}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

function BanUserDialog({ userId, userEmail, onClose, onSave, onError }: BanUserDialogProps) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleBan = async () => {
    setSaving(true);
    try {
      await banUser(userId, reason.trim() || undefined);
      onSave();
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to ban user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={saving ? undefined : onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Ban {userEmail}?</DialogTitle>
        <CardBody>
          This will immediately block all access for this account.
        </CardBody>
        <FormField>
          <FormLabel>Reason (optional, internal only)</FormLabel>
          <FormInput
            type="text"
            placeholder="e.g. spam abuse, free tier fraud"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </ActionButton>
          <ActionButton $variant="danger" onClick={handleBan} disabled={saving}>
            {saving ? 'Banning...' : 'Ban User'}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

export default BackofficeUserDetail;
